import React from 'react';
import { getDimensionColor } from '../../design-system/tokens';

/**
 * A labeled score bar for a single dimension.
 * Used in score cards, case results, report details.
 */
export function DimensionBar({ dimensionKey, label, score, showDriverHint, driverText, isActive, onClick }) {
  const color = getDimensionColor(dimensionKey);
  const scoreColor = score >= 70 ? 'var(--msml-success)' : score >= 50 ? 'var(--msml-warning)' : 'var(--msml-danger)';

  return (
    <button
      onClick={onClick}
      className={`text-left p-3 rounded-lg border transition-all duration-200 w-full ${
        isActive
          ? 'border-white/20 bg-white/[0.04]'
          : 'border-white/[0.06] bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.03]'
      }`}
      data-testid={`dimension-${dimensionKey}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--msml-text-muted)]">{label}</span>
        <span className="text-sm font-semibold" style={{ color: scoreColor }}>{Math.round(score)}</span>
      </div>
      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${score}%`, backgroundColor: scoreColor }}
        />
      </div>
      {showDriverHint && driverText && (
        <p className="text-[10px] text-[var(--msml-text-muted)] mt-1.5 line-clamp-1">{driverText}</p>
      )}
    </button>
  );
}

/**
 * Grid of DimensionBars.
 */
export function DimensionGrid({ dimensions, activeDimension, onDimensionClick }) {
  return (
    <div className="grid grid-cols-2 gap-2.5" data-testid="dimension-grid">
      {dimensions.map(dim => (
        <DimensionBar
          key={dim.key}
          dimensionKey={dim.key}
          label={dim.label}
          score={dim.score}
          showDriverHint={dim.drivers?.length > 0}
          driverText={dim.drivers?.[0]?.reason}
          isActive={activeDimension === dim.key}
          onClick={() => onDimensionClick?.(dim.key)}
        />
      ))}
    </div>
  );
}
